import { useCachedGet } from "@/lib/cache";
import { offerValue } from "@/components/listing/OfferPrice";

const RATING = { good: ["جيد", "Good"], very_good: ["جيد جداً", "Very Good"], excellent: ["ممتاز", "Excellent"] };

// Field visible when its showIf condition holds and its parent field is visible too (chained).
const HIDDEN_CONTACT = new Set(["contact_phone", "whatsapp", "phone", "contact_whatsapp"]);
// Detail-page only labels (form keeps the original). null = hidden on the detail page.
const AREAS = { ar: "المناطق التي يصلون إليها", en: "Areas they cover" };
const DETAIL_OVERRIDES = {
  cleaning_company: {
    housing_types: null,
    villa_price: { ar: "سعر تنظيف الفيلا", en: "Villa cleaning price", unit: "aed" },
    apartment_price: { ar: "سعر تنظيف الشقة", en: "Apartment cleaning price", unit: "aed" },
    service_areas: { ar: "المناطق التي يصلون إليها", en: "Areas they cover" },
  },
  cleaning_daily: {
    hourly_rate: { ar: "سعر العامل بالساعة", en: "Worker hourly rate", unit: "aed" },
    service_areas: AREAS,
  },
  movers: { service_areas: AREAS },
  laundry: { service_areas: AREAS },
  car_wash: { on_site_areas: AREAS },
};
const daysAr = (n) => (n === 1 ? "يوم واحد متبقٍ" : n === 2 ? "يومان متبقيان" : n <= 10 ? `${n} أيام متبقية` : `${n} يوماً متبقياً`);
// "5 أيام متبقية · ينتهي 02/11/2026" (UAE time)
export const endsText = (until, lang) => {
  const left = Math.max(0, Math.ceil((new Date(until).getTime() - Date.now()) / 86400000));
  const date = new Date(until).toLocaleDateString("en-GB", { timeZone: "Asia/Dubai" });
  return lang === "en" ? `${left} day${left === 1 ? "" : "s"} left · Ends ${date}` : `${daysAr(left)} · ينتهي ${date}`;
};
const condOk = (c, vals) => {
  const picked = String(vals[c.key] ?? "").split(",").filter(Boolean);
  return c.notEmpty ? picked.length > 0 : picked.includes(c.equals);
};

export function isShown(d, defs, vals) {
  if (!d.showIf) return true;
  if (!condOk(d.showIf, vals) || (d.showIfAlso && !condOk(d.showIfAlso, vals))) return false;
  const p = defs.find((x) => x.key === d.showIf.key);
  return !p || isShown(p, defs, vals);
}

export function fmtField(meta, f, v, lang) {
  const L = (o) => (o ? (lang === "ar" ? o.ar : o.en) : "");
  const sep = lang === "ar" ? "، " : ", ";
  const country = (en) => { const c = meta.countries.find((x) => x[1] === en); return c ? (lang === "ar" ? c[0] : c[1]) : en; };
  if (v === meta.unknown_value) return f?.unknownLabel ? L(f.unknownLabel) : lang === "ar" ? "غير معروف / لا يوجد" : "Unknown / N/A";
  if (f.type === "select") return L(f.options?.find((o) => o.id === v)) || v;
  if (f.type === "multiselect") return String(v).split(",").map((id) => L(f.options?.find((o) => o.id === id)) || id).join(sep);
  if (f.type === "country") return country(v);
  if (f.unit === "aed") { const n = Number(v).toLocaleString("en"); return lang === "ar" ? `${n} درهم` : `AED ${n}`; }
  if (f.type === "countries") return String(v).split(",").map(country).join(sep);
  if (f.type === "priced_services") {
    const name = (id) => (id === "other" && v.other_text ? v.other_text : L(f.options?.find((o) => o.id === id)) || id);
    return v.mode === "all" ? `${v.selected.map(name).join("، ")} — AED ${v.all_price}` : v.selected.map((id) => `${name(id)}: AED ${v.prices?.[id] || "-"}`).join(" · ");
  }
  return String(v);
}

// Ad detail rows built from the mobile field definitions (/api/meta subcat_fields): [key, label, value, fieldType].
export function buildAdRows(meta, listing, lang) {
  const L = (o) => (o ? (lang === "ar" ? o.ar : o.en) : "");
  const T = (ar, en) => (lang === "ar" ? ar : en);
  const tr = listing.tr?.[lang] || {};
  const rows = [];
  const ages = [...meta.condition_age_new, ...meta.condition_used];
  if (listing.condition && !listing.business_name) rows.push(["condition", T("الحالة", "Condition"), listing.condition === "new" ? T("جديد", "New") : T("مستعمل", "Used")]);
  const age = listing.age || listing.usage_duration;
  if (age) rows.push(["age", listing.age ? T("عمر المنتج", "Product age") : T("مدة الاستعمال", "Usage duration"), L(ages.find((o) => o.id === age)) || age]);
  if (RATING[listing.rating]) rows.push(["rating", T("الجودة", "Quality"), RATING[listing.rating][lang === "ar" ? 0 : 1]]);
  const defs = meta.subcat_fields[listing.subcategory] || [];
  const vals = listing.fields || {};
  const over = DETAIL_OVERRIDES[listing.subcategory] || {};
  const dailyPct = ["cleaning_daily", "movers"].includes(listing.subcategory) && !Object.keys(listing.dw_offers || {}).length && listing.discount_percent > 0 && (!listing.offer_until || new Date(listing.offer_until) > new Date()) ? Number(listing.discount_percent) : 0;
  const cut = (n) => ({ pct: dailyPct, original: Number(n), price: Math.round(Number(n) * (1 - dailyPct / 100) * 100) / 100 });
  const offerEnds = dailyPct && listing.offer_until ? endsText(listing.offer_until, lang) : "";
  defs.forEach((f0) => {
    if (HIDDEN_CONTACT.has(f0.key) || over[f0.key] === null) return; // numbers only via the contact/WhatsApp buttons
    if (listing.category === "home_services" && f0.key === "price_mode") {
      const aedN = (n) => (lang === "ar" ? `${Number(n).toLocaleString("en")} درهم` : `AED ${Number(n).toLocaleString("en")}`);
      if (vals.price_mode === "on_call") rows.push(["f_price_mode", T("سعر الخدمة", "Service price"), T("السعر عند الاتصال", "Price on call"), "text"]);
      const base = Number(listing.original_price || listing.price);
      // Older ads: one price for the whole service (no per-service prices yet).
      if (vals.price_mode === "fixed" && !vals.services?.selected?.length && base > 0)
        rows.push(["f_price_mode", T("سعر الخدمة", "Service price"), dailyPct ? { ...offerValue(cut(base), aedN, lang), inline: true, until: offerEnds } : aedN(base), dailyPct ? "dwoffer" : "price"]);
      return;
    }
    const f = over[f0.key] ? { ...f0, ...over[f0.key] } : f0;
    const v = vals[f.key];
    if (f.type === "hours_package") {
      if (vals.hourly_rate === meta.unknown_value || !isShown(f, defs, vals)) return;
      const aedN = (n) => (lang === "ar" ? `${Number(n).toLocaleString("en")} درهم` : `AED ${Number(n).toLocaleString("en")}`);
      const priced = (name, n) => (Number(n) > 0 ? (dailyPct ? { name, ...offerValue(cut(n), aedN, lang), until: offerEnds } : { name, plain: aedN(n) }) : name);
      const pm = String(v || "").split(",");
      const items = [];
      if (pm.includes("hours") && vals.package_hours) items.push(priced(T(`عدد الساعات: ${vals.package_hours}`, `Hours: ${vals.package_hours}`), vals.package_price));
      if (pm.includes("full_day")) items.push(priced(T("يوم كامل", "Full day"), vals.package_day_price || (!pm.includes("hours") && vals.package_price)));
      if (!v && vals.package_hours) items.push(String(vals.package_hours));
      if (items.length && dailyPct && listing.offer_days) items.push({ name: T("عدد أيام العرض", "Offer duration"), plain: T(`${listing.offer_days} ${listing.offer_days > 10 || listing.offer_days < 3 ? "يوم" : "أيام"}`, `${listing.offer_days} day${listing.offer_days > 1 ? "s" : ""}`) });
      if (items.length) rows.push([`f_${f.key}`, T("عرض خاص", "Special offer"), items, "special"]);
      return;
    }
    if (dailyPct && f.key === "hourly_rate" && Number(v) > 0 && isShown(f, defs, vals)) return rows.push([`f_${f.key}`, L(f), { ...offerValue(cut(v), (n) => fmtField(meta, f, n, lang), lang), inline: true, until: offerEnds }, "dwoffer"]);
    if (f.modeKey && vals[f.modeKey] === "none") return rows.push([`f_${f.key}`, L(f), L(f.unknownLabel), f.type]);
    if (v == null || v === "" || (typeof v === "object" && !v.selected?.length) || !isShown(f, defs, vals)) return;
    if (defs.some((c) => c.unit && c.showIf?.key === f.key && c.showIf.equals === v && vals[c.key])) return;
    const off = listing.dw_offers?.[{ office_fee: "office", sponsor_price: "sponsor", villa_price: "villa", apartment_price: "apartment" }[f.key]];
    if (off) {
      const p = (n) => fmtField(meta, f, n, lang);
      const until = new Date(off.until).toLocaleDateString("en-GB");
      return rows.push([`f_${f.key}`, L(f), { ...offerValue(off, p, lang), until: (lang === "en" ? "Ends " : "ينتهي في ") + until }, "dwoffer"]);
    }
    const pfx = f.key.startsWith("services_") ? `${f.key.slice(9)}_` : listing.subcategory === "movers" ? "svc_" : "";
    if (listing.subcategory === "car_wash" && f.key === "package") return;
    // Home services: bullet list "service — price" (price in green), same as the mobile app.
    if (f.type === "priced_services" && listing.category === "home_services" && listing.subcategory !== "pet_care") {
      const name = (id) => (id === "other" && v.other_text ? v.other_text : L(f.options?.find((o) => o.id === id)) || id);
      const aed = (n) => (lang === "ar" ? `${Number(n).toLocaleString("en")} درهم` : `AED ${Number(n).toLocaleString("en")}`);
      const onCall = vals.hs_price_mode === "on_call" || vals.price_mode === "on_call";
      const callTxt = T("السعر عند الاتصال", "Price on call");
      const price = (n) => (Number(n) > 0 ? (dailyPct ? { ...offerValue(cut(n), aed, lang), until: offerEnds } : aed(n)) : onCall ? callTxt : null);
      const withEnds = (o) => ({ ...offerValue(o, aed, lang), until: endsText(o.until, lang) });
      const items = v.selected.map((id) => {
        const o = listing.dw_offers?.[pfx + id];
        return [name(id), v.mode === "all" ? null : o ? withEnds(o) : price(v.prices?.[id])];
      });
      const hasAll = (v.mode === "all" || v.mode === "both") && Number(v.all_price) > 0;
      const allLabel = f.combinedWhenAll ? L(f.combinedWhenAll) : f.allLabel ? L(f.allLabel) : T("سعر جميع الخدمات", "Price of all services");
      const allOff = listing.dw_offers?.svc_all || listing.dw_offers?.[`${pfx}all`];
      const all = hasAll ? (allOff ? withEnds(allOff) : price(v.all_price)) : null;
      const pkgId = listing.subcategory === "car_wash" && f.key.startsWith("services_") ? f.key.slice(9) : null;
      const pkgName = pkgId && (L(defs.find((x) => x.key === "package")?.options?.find((o) => o.id === pkgId)) || (pkgId === "premium" ? T("باقة الغسيل المميز", "Premium wash package") : T("باقة الغسيل العادي", "Regular wash package")));
      return rows.push([`f_${f.key}`, pkgName || (listing.subcategory === "movers" ? T("سعر الخدمة", "Service price") : L(f).replace(/\s*\((درهم|AED)\)/, "")), { items, all, allLabel, highlight: !!f.combinedWhenAll, pkg: pkgId }, "services"]);
    }
    if (listing.category === "domestic_workers" && (f.type === "multiselect" || f.type === "countries")) return rows.push([`f_${f.key}`, L(f), String(v).split(",").filter(Boolean).map((x) => fmtField(meta, f, x, lang)), f.type]);
    rows.push([`f_${f.key}`, L(f), f.translatable && tr[f.key] ? tr[f.key] : fmtField(meta, f, v, lang), (f.unit === "aed" || f.priceOrCall || /price|fee|rate/.test(f.key)) && Number(v) > 0 ? "price" : f.type]);
  });
  return rows;
}
